import { useQuery, skipToken } from '@tanstack/react-query';
import type { BoxStatus } from '@citibox-panel/api-client';

export interface UseBoxStatus {
  status: BoxStatus | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

// Keyed by BOTH ids: the read is box-first, but the resolved case is relative to the delivery on
// screen, so two deliveries of one «buzón» must not share a cache entry — unlike ['locker-log', boxId],
// which is shared by design because it really is the same log. EAGER once the detail resolves: the
// notice is always visible, unlike the log, which waits for its unfold. No throwOnError — a failure
// degrades to an inline strip so the rest of the detail keeps working.
//
// `retry: false` overrides the client's default: until app-service ships this endpoint every detail
// with a «buzón» gets a 404 a retry cannot fix, so retrying would only double the doomed traffic and
// hold the skeleton across both attempts. The strip's «Reintentar» is the retry.
//
// The boxId gate is belt to the mount guard's braces — the card only mounts this when the delivery
// has a «buzón», and that conditional mount is the authoritative «no box, no request». This keeps the
// hook honest if it is ever reused without that guard; do not drop one thinking the other covers it.
export function useBoxStatus(
  boxId: string | undefined,
  deliveryId: string,
  fetchBoxStatus: (boxId: string, deliveryId: string) => Promise<BoxStatus>,
): UseBoxStatus {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['box-status', boxId, deliveryId],
    queryFn: boxId !== undefined ? () => fetchBoxStatus(boxId, deliveryId) : skipToken,
    retry: false,
  });
  return { status: data, isPending, isError, refetch: () => void refetch() };
}
